import React from 'react'
import styled, { css } from 'styled-components'
import Button from '../Button'
import ButtonAndText from '../ButtonAndText'
import ButtonGroup from '../ButtonGroup'
import ClientList from '../ClientList'
import ContactBox from '../ContactBox/custom'
import ContactBoxes from '../ContactBoxes'
import Container from '../../Layout/Container'
import CTA from '../../sections/CTA'
import FeaturedPartners from '../../sections/FeaturedPartners'
import FeatureList from '../FeatureList'
import Form from '../Form'
import FormSection from '../../sections/Form'
import GeneralSection from '../../sections/GeneralSection'
import HistoryTimeline from '../../sections/HistoryTimeline'
import HtmlContent from '../HtmlContent'
import ImageAndHeader from '../../sections/ImageAndHeader'
import ImageLink from '../ImageLink'
import LanguageCards from '../LanguageCards'
import MemberList from '../MemberList'
import NumberedList from '../NumberedList'
import PageHeader from '../../sections/PageHeaderSection'
import PostBlogEmbeddableCTA from '../BlogPostCTA'
import PostBlogEmbeddableForm from '../BlogPostForm'
import PostBlogEmeddableCodeSnippet from '../BlogPostCodeSnippet'
import QuoteSection from '../../sections/QuoteSection'
import SingleQuote from '../SingleQuote'
import TabList from '../TabList'
import TextHeader from '../TextHeader'
import VideoEmbed from '../VideoEmbed'

const StyledMainHeader = styled.main`
  align-items: center;
  display: flex;
  flex-direction: column;
`
const StyledRightColumn = styled.div`
  ul {
    margin-left: -24px;
  }

  ${({ sameStart }) =>
    sameStart &&
    css`
      height: 100%;
    `}

  ${({ theme }) => theme.media.desktop} {
    padding-left: 42px;

    ul {
      margin-left: 0;
    }
  }
`

const StyledLeftColumn = styled.div`
  padding: 16px 0;

  ${({ sticky }) =>
    sticky &&
    css`
      height: 100%;

      div {
        height: 100%;
      }

      ${({ theme }) => theme.media.desktop} {
        img {
          position: sticky;
          top: 200px;
        }
      }
    `}

  ${({ sameStart }) =>
    sameStart &&
    css`
      height: 100%;
    `}

  ${({ theme }) => theme.media.desktop} {
    padding: 0 24px 0 0;
  }
`

const StyledClientListWrapper = styled.div`

`

const StyledSingleImage = styled.img`
  max-width: 100%;
`

const isSingleImage = item => item.sys.contentType.sys.id === 'singleImage'
const isNotSingleImage = item => item.sys.contentType.sys.id !== 'singleImage'

const ComponentRenderer = ({ tag, children, cmsData }) => {
  const components = {
    button: Button,
    buttonAndText: ButtonAndText,
    buttonGroup: ButtonGroup,
    clientList: ClientList,
    companyHistoryTimeline: HistoryTimeline,
    companyTeam: MemberList,
    container: Container,
    contactBox: ContactBox,
    CTA: CTA,
    embeddedCodeSnippet: PostBlogEmeddableCodeSnippet,
    embeddedCta: PostBlogEmbeddableCTA,
    embeddedForm: PostBlogEmbeddableForm,
    FeaturedPartners: FeaturedPartners,
    features: FeatureList,
    form: Form,
    Form: FormSection,
    General: GeneralSection,
    htmlContent: HtmlContent,
    imageLink: ImageLink,
    languageCards: LanguageCards,
    numberedList: NumberedList,
    pageHeader: PageHeader,
    Quote: QuoteSection,
    singleQuoteComponent: SingleQuote,
    textHeader: TextHeader,
    timedSlider: TabList,
    twoColumns: ImageAndHeader,
    videoEmbed: VideoEmbed,
  }

  const logoInHeader = cmsData ? true : false
  const removePadding = cmsData ? true : false
  const sameStart = cmsData ? true : false
  const smallHeight = cmsData ? true : false
  const TagName = components[tag || null]

  if (TagName === PageHeader) {
    const items = cmsData.items || []
    return (
      <PageHeader 
        logoInHeader={cmsData ? cmsData.logoInHeader : logoInHeader}
        removePadding={cmsData ? cmsData.removePadding : removePadding}
        smallHeight={cmsData ? cmsData.smallHeight : smallHeight}
      >
        <StyledMainHeader>
          {items.filter(isSingleImage).map((item, index) => (
              <ComponentRenderer
                cmsData={item.fields}
                key={index}
                tag={item.sys.contentType.sys.id}
              />
          ))}
          {items.filter(isNotSingleImage).map((item, index) => (
            <ComponentRenderer
              cmsData={item.fields}
              tag={item.sys.contentType.sys.id}
              key={index}
            />
          ))}
        </StyledMainHeader>
      </PageHeader>
    )
  } else if (tag === 'singleImage') {
    return (
      <div className="mailgun-image-wrapper">
        <StyledSingleImage
          alt={cmsData.image.fields.title || cmsData.image.fields.file.fileName}
          src={cmsData.image.fields.file.url}
        />
      </div>
    )
  } else if (TagName === ImageAndHeader) {
    return (
      <ImageAndHeader
        cmsData={cmsData}
        rightColumn={{
          width: cmsData.rightColumn.fields.width,
          data: cmsData.rightColumn.fields.items.map(item => {
            return (
              <StyledRightColumn
                key={item.sys.id}
                sameStart={cmsData ? cmsData.sameStart : sameStart}
              >
                <ComponentRenderer
                  tag={item.sys.contentType.sys.id}
                  cmsData={item.fields}
                />
              </StyledRightColumn>
            )
          }),
        }}
        leftColumn={{
          width: cmsData.leftColumn.fields.width,
          data: cmsData.leftColumn.fields.items.map(item => {
            return (
              <StyledLeftColumn
                key={item.sys.id}
                sameStart={cmsData ? cmsData.sameStart : sameStart}
                sticky={item.fields.stickyImage}
              >
                <ComponentRenderer
                  tag={item.sys.contentType.sys.id}
                  cmsData={item.fields}
                />
              </StyledLeftColumn>
            )
          }),
        }}
      />
    )
  } else if (TagName === ButtonGroup) {
    return (
      <ButtonGroup>
        {cmsData.items.map(item => {
          return (
            <ComponentRenderer
              key={item.sys.id}
              tag={item.sys.contentType.sys.id}
              cmsData={item.fields}
            />
          )
        })}
      </ButtonGroup>
    )
  } else if (TagName === ClientList) {
    return (
      <Container>
        <StyledClientListWrapper>
          <TagName cmsData={cmsData}>{children}</TagName>
        </StyledClientListWrapper>
      </Container>
    )
  } else if (TagName === TabList) {
    return (
      <Container>
        <TagName cmsData={cmsData}>{children}</TagName>
      </Container>
    )
  } else if (TagName === Container) {
    if (cmsData.sectionType === 'None') {
      return <TagName cmsData={cmsData}>{children}</TagName>
    } else if (cmsData.sectionType === 'ContactBoxes') {
      return <ContactBoxes>{children}</ContactBoxes>
    } else {
      return (
        <TagName cmsData={cmsData}>
          <ComponentRenderer cmsData={cmsData} tag={cmsData.sectionType}>
            {children}
          </ComponentRenderer>
        </TagName>
      )
    }
  } else if (TagName) {
    return <TagName cmsData={cmsData}>{children}</TagName>
  } else {
    return <div>{tag}</div>
  }
}

export default ComponentRenderer
